import { z } from 'zod';
import { optionalPhoneSchema } from '@/components/ui/phone-input';

export const PASSWORD_RULES = [
  { test: (v: string) => v.length >= 8, label: 'At least 8 characters long' },
  {
    test: (v: string) => /[a-zA-Z]/.test(v),
    label: 'Contains at least one letter',
  },
  { test: (v: string) => /\d/.test(v), label: 'Contains at least one number' },
  {
    test: (v: string) => /[^a-zA-Z0-9]/.test(v),
    label: 'Contains at least one special character',
  },
] as const;

export const organizationTypeEnum = [
  'Sponsor',
  'Contract Research Organization (CRO)',
  'Site',
  'Institution',
  'Site Network/Site Management Organization (SMO)',
] as const;

export const RegistrationSchema = z
  .object({
    email: z
      .string()
      .min(1, 'This field is required')
      .email('Invalid email address'),
    password: z.string().superRefine((val, ctx) => {
      const failures = PASSWORD_RULES.filter(r => !r.test(val));
      if (failures.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: failures.map(r => r.label).join('\n'),
        });
      }
    }),
    passwordConfirmation: z.string().min(1, 'This field is required'),
    firstName: z.string().min(1, 'This field is required'),
    middleInitial: z.string().optional(),
    lastName: z.string().min(1, 'This field is required'),
    organizationName: z.string().optional(),
    organizationType: z.enum(organizationTypeEnum).optional(),
    mobilePhone: optionalPhoneSchema,
    countryOfResidence: z.string().optional(),
    countryOfBusiness: z.string().optional(),
    termsAndCondition: z
      .object({
        accepted: z.boolean(),
        version: z.string().optional(),
        acceptedAt: z.string().optional(), // ISO string timestamp
      })
      .refine(data => data.accepted === true, {
        message: 'You must accept the terms and conditions',
      }),
    recaptchaToken: z
      .string()
      .min(1, 'Please complete the reCAPTCHA verification'),
  })
  .refine(data => data.password === data.passwordConfirmation, {
    message: 'Passwords do not match',
    path: ['passwordConfirmation'],
  });

export type RegistrationFormData = z.infer<typeof RegistrationSchema>;
